import { useState, useCallback, useMemo, useEffect } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { escapeContextLabel } from 'tripPlanner/utils/contextTag'

export const DRAWER_CONTEXT_PREFIX = 'The user is asking about: '

interface UseDrawerAskAIArgs {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
  contextLabel: string
  isOpen: boolean
  /** Formatted hotel details to include as context when asking from a hotel drawer. */
  hotelDetails?: string
}

export interface DrawerSessionPair {
  question: MessageItem
  answer: MessageItem | null
}

interface UseDrawerAskAIReturn {
  handleSubmit: (message: string) => Promise<void>
  latestResponse: MessageItem | null
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  hasActiveSession: boolean
}

export function useDrawerAskAI({
  submitMessage,
  messages,
  isStreaming,
  contextLabel,
  isOpen,
  hotelDetails,
}: UseDrawerAskAIArgs): UseDrawerAskAIReturn {
  // Capture the current message count when the drawer opens.
  // Only messages added after this point belong to the current session.
  // messages.length is intentionally excluded from deps to avoid resetting mid-session.
  const [sessionStartIndex, setSessionStartIndex] = useState(() => messages.length)

  useEffect(() => {
    if (isOpen) {
      setSessionStartIndex(messages.length)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const contextMarker = `${DRAWER_CONTEXT_PREFIX}${contextLabel}`

  const handleSubmit = useCallback(async(message: string) => {
    const hotelDetailsPart = hotelDetails ? `\n${hotelDetails}` : ''
    const wrappedMessage = `${message}\n\n<Context label="${escapeContextLabel(contextLabel)}">${contextMarker}${hotelDetailsPart}\nRespond in plain text only. Do not use UI elements, cards, interactive components, or bold text. Keep your response under 150 words. Be direct and concise. Do not add extra context or elaboration beyond what is asked. Do not ask any follow-up questions.</Context>`
    await submitMessage(wrappedMessage)
  }, [submitMessage, contextLabel, contextMarker, hotelDetails])

  const { latestResponse, sessionHistory, isResponseStreaming, isWaitingForResponse } = useMemo(() => {
    const sessionMessages = messages.slice(sessionStartIndex)

    const pairs: Array<DrawerSessionPair> = []

    for (let i = 0; i < sessionMessages.length; i++) {
      const msg = sessionMessages[i]
      if (msg.role === 'user' && msg.rawContent?.includes(contextMarker)) {
        const nextMsg = sessionMessages[i + 1]
        const answer = nextMsg?.role === 'assistant' ? nextMsg : null
        pairs.push({ question: msg, answer })
      }
    }

    const latestPair = pairs.length > 0 ? pairs[pairs.length - 1] : undefined
    const latestResponse = latestPair?.answer ?? null

    // The response is streaming when the global stream is active and the latest response
    // is the last message in the full array (i.e. the stream is for our message)
    const lastMessage = messages[messages.length - 1]
    const isResponseStreaming = isStreaming && latestResponse !== null && lastMessage?.id === latestResponse.id

    // Waiting state: we submitted but the assistant message hasn't appeared yet.
    // Also verify our question is the tail of the global array, so a main-chat
    // stream doesn't trigger a false loading state in the drawer.
    const isWaitingForResponse = isStreaming && latestPair?.answer === null && lastMessage?.id === latestPair?.question.id

    return { latestResponse, sessionHistory: pairs, isResponseStreaming, isWaitingForResponse }
  }, [messages, sessionStartIndex, contextMarker, isStreaming])

  return {
    handleSubmit,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession: sessionHistory.length > 0,
  }
}
